<template>
  <div class="popup-manager">
    <v-dialog
      v-model="isDialogVisible"
      :fullscreen="isMobile"
      :max-width="isMobile ? '100%' : '500px'"
      transition="dialog-bottom-transition"
      content-class="modern-popup-dialog"
      :scrim="false"
      persistent>
      <div class="glass-overlay" v-if="isDialogVisible" @click="minimizeDialog"></div>

      <v-card v-if="popupsToShow.length > 0" class="d-flex flex-column popup-card elevation-10" color="#1e1e24">
        <div class="popup-header d-flex align-center justify-space-between px-4 pt-4 pb-2 flex-shrink-0">
          <v-chip small color="rgba(255,255,255,0.1)" text-color="white" class="font-weight-bold backdrop-blur">
            NEWS {{ currentIndex + 1 }} / {{ popupsToShow.length }}
          </v-chip>

          <v-btn icon @click="minimizeDialog" class="close-btn backdrop-blur">
            <v-icon color="white">mdi-chevron-down</v-icon>
          </v-btn>
        </div>

        <v-carousel
          v-model="currentIndex"
          :show-arrows="!isMobile && popupsToShow.length > 1"
          hide-delimiters
          height="100%"
          :continuous="false"
          class="popup-carousel flex-grow-1">
          <template v-slot:prev="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" fab small class="nav-btn left backdrop-blur">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
          </template>
          <template v-slot:next="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" fab small class="nav-btn right backdrop-blur">
              <v-icon>mdi-arrow-right</v-icon>
            </v-btn>
          </template>

          <v-carousel-item v-for="(popup, index) in popupsToShow" :key="popup._id">
            <div class="popup-item-layout d-flex flex-column" style="height: 100%">
              <div class="image-wrapper flex-shrink-0 mx-4 mt-2 elevation-5 rounded-xl overflow-hidden">
                <v-img :src="getImageUrl(popup)" :aspect-ratio="16 / 9" width="100%" class="bg-grey-darken-3" cover>
                  <template v-slot:placeholder>
                    <div class="fill-height d-flex justify-center align-center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                  </template>
                  <div class="image-gradient"></div>
                </v-img>
              </div>

              <div class="text-content-wrapper flex-grow-1 pa-5 overflow-y-auto d-flex flex-column">
                <div>
                  <h2 class="text-h5 white--text font-weight-bold mb-3">{{ popup.title }}</h2>
                  <div class="text-body-1 grey--text text--lighten-1">
                    <div class="html-popup-text" v-html="popup.text"></div>
                  </div>
                </div>
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>

        <div
          v-if="isMobile && popupsToShow.length > 1"
          class="mobile-swipe-guide flex-shrink-0"
          aria-live="polite"
          :aria-label="`News ${currentIndex + 1} von ${popupsToShow.length}. Seitlich wischen für weitere News.`">
          <div class="swipe-progress" aria-hidden="true">
            <span
              v-for="(popup, index) in popupsToShow"
              :key="`popup-progress-${popup._id || index}`"
              class="swipe-progress__dot"
              :class="{ 'is-active': index === currentIndex }"></span>
          </div>
          <div class="swipe-guide__copy">
            <v-icon class="swipe-guide__icon" size="18">mdi-gesture-swipe-horizontal</v-icon>
            <span>Seitlich wischen für weitere News</span>
          </div>
        </div>

        <div class="popup-footer pa-4 pt-2 flex-shrink-0">
          <v-expand-transition>
            <div v-show="currentPopup.link && currentPopup.linkText" class="mb-3">
              <v-btn block x-large class="rounded-lg font-weight-bold cta-premium-btn" elevation="10" @click="handleActionLink(currentPopup)">
                <span class="text-truncate">{{ currentPopup.linkText }}</span>
                <v-icon right color="black">
                  {{ isExternal(currentPopup.link) ? "mdi-open-in-new" : "mdi-arrow-right-circle" }}
                </v-icon>
              </v-btn>
            </div>
          </v-expand-transition>

          <v-btn
            block
            x-large
            :color="currentPopup.link && currentPopup.linkText ? 'rgba(255,255,255,0.08)' : 'primary'"
            :class="[currentPopup.link && currentPopup.linkText ? 'white--text' : '', 'rounded-lg font-weight-bold transition-fast-in-fast-out']"
            elevation="0"
            @click="handleMainAction">
            {{ isLastPopup ? "Schliessen" : "Nächster Beitrag" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-scale-transition>
      <div v-if="isHome && isMinimized && popupsToShow.length > 0 && !disabled" class="minimized-fab-container" @click="restoreDialog">
        <v-badge color="red" :content="unseenCount" :value="unseenCount > 0" overlap bordered offset-x="20" offset-y="20">
          <v-btn fab large color="#1e1e24" class="elevation-10 notification-fab">
            <div v-if="hasUnseenPopups" class="pulse-ring"></div>
            <v-icon color="primary">mdi-bell-ring-outline</v-icon>
          </v-btn>
        </v-badge>
        <span class="fab-label mt-1">News</span>
      </div>
    </v-scale-transition>
  </div>
</template>

<script>
import api from "@/api"
import { mapActions, mapState } from "vuex"

const STORAGE_KEY_SEEN = "roody_seen_popup_ids"

export default {
  name: "PopupDialog",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      seenIds: JSON.parse(localStorage.getItem(STORAGE_KEY_SEEN) || "[]"),
      popupsToShow: [],
      isDialogVisible: false,
      isMinimized: false,
      currentIndex: 0,
      hasUnseenPopups: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["selectedStudio"]),
    unseenCount() {
      return this.popupsToShow.filter((p) => !this.seenIds.includes(p._id)).length
    },
    isHome() {
      return this.$route.name === "Home"
    },
    currentPopup() {
      return this.popupsToShow[this.currentIndex] || {}
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    isLastPopup() {
      return this.currentIndex === this.popupsToShow.length - 1
    },
  },
  watch: {
    disabled(val) {
      if (val) {
        this.isDialogVisible = false
        this.isMinimized = false
      }
    },
    isDialogVisible(val) {
      this.setNavigationbarVisibility(!val)
    },
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    isExternal(linkStr) {
      if (!linkStr) return false
      return linkStr.startsWith("http")
    },

    handleActionLink(popup) {
      if (!popup.link) return

      if (this.isExternal(popup.link)) {
        // Externer Link: Öffnet sich in neuem Fenster/Tab
        window.open(popup.link, "_blank")
      } else {
        // Interner Link: Über Vue-Router steuern
        const targetPath = popup.link.startsWith("/") ? popup.link : "/" + popup.link

        // Nur pushen, wenn wir nicht schon auf der Seite sind (verhindert NavigationDuplicated Error)
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath)
        }

        // Optional: Popup direkt schliessen, wenn ein interner Link geklickt wird
        this.minimizeDialog()
      }
    },
    async fetchAndShowPopups() {
      try {
        const popups = await api.PopupService.getActive()
        if (popups && popups.length > 0) {
          this.popupsToShow = popups.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          this.checkSeenStatusAndDecideView()
        }
      } catch (e) {
        console.error("Popup Error:", e)
      }
    },

    checkSeenStatusAndDecideView() {
      const newPopups = this.popupsToShow.filter((p) => !this.seenIds.includes(p._id))
      this.hasUnseenPopups = newPopups.length > 0

      setTimeout(() => {
        if (this.disabled) return
        if (this.hasUnseenPopups) {
          this.isDialogVisible = true
          this.isMinimized = false
        } else {
          this.isDialogVisible = false
          this.isMinimized = true
        }
      }, 1000)
    },

    markAllAsSeen() {
      const currentIds = this.popupsToShow.map((p) => p._id)
      const updatedSet = new Set([...this.seenIds, ...currentIds])
      this.seenIds = [...updatedSet]
      localStorage.setItem(STORAGE_KEY_SEEN, JSON.stringify(this.seenIds))
      this.hasUnseenPopups = false
    },

    getImageUrl(popup) {
      if (popup.image && popup.image.length > 2) {
        return "https://app.wellcomefit.ch/img/" + popup.image
      }
      const code = this.selectedStudio?.code?.toUpperCase() || "DEFAULT"
      return `https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_${code}_BLACK.png`
    },

    handleMainAction() {
      if (this.isLastPopup) {
        this.minimizeDialog()
      } else {
        this.currentIndex++
      }
    },

    minimizeDialog() {
      this.markAllAsSeen()
      this.isDialogVisible = false
      setTimeout(() => {
        this.isMinimized = true
      }, 300)
    },

    restoreDialog() {
      this.isMinimized = false
      this.isDialogVisible = true
    },
  },
  created() {
    this.fetchAndShowPopups()
  },
}
</script>

<style lang="scss" scoped>
::v-deep .html-popup-text p {
  margin-bottom: 0px !important;
}
/* --- Glass Overlay --- */
.glass-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: -1;
}

/* --- Dialog & Card Styling --- */
::v-deep .modern-popup-dialog {
  box-shadow: none !important;
  /* Auf Mobile Fullscreen */
  @media (max-width: 600px) {
    margin: 0 !important;
    max-height: 100% !important;
    height: 100% !important;
  }
}

.popup-card {
  border-radius: 24px !important;
  background: linear-gradient(145deg, #1e1e24 0%, #121212 100%);
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* --- FIX FÜR DESKTOP --- */
  /* Wir erzwingen eine Höhe auf Desktop, damit das Carousel (height=100%) funktioniert */
  @media (min-width: 601px) {
    height: 700px;
    max-height: 90vh; /* Sicherheitsnetz für kleine Laptops */
  }

  /* --- FIX FÜR MOBILE --- */
  @media (max-width: 600px) {
    height: 100%;
    border-radius: 0 !important;
  }
}

/* --- Carousel --- */
.popup-carousel {
  /* Nimmt den Platz ein, den die Card zur Verfügung stellt */
  width: 100%;
}

.mobile-swipe-guide {
  display: flex;
  min-height: 2.75rem;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.25rem 1rem 0.5rem;
  color: rgba(255, 255, 255, 0.68);
}

.swipe-progress {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 0.3125rem;
}

.swipe-progress__dot {
  width: 0.375rem;
  height: 0.375rem;
  flex: 0 0 auto;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.24);
  transition: width 240ms ease, background-color 240ms ease, box-shadow 240ms ease;
}

.swipe-progress__dot.is-active {
  width: 1.25rem;
  background: #7ee5a6;
  box-shadow: 0 0 0.75rem rgba(88, 211, 136, 0.28);
}

.swipe-guide__copy {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.swipe-guide__icon {
  color: #7ee5a6 !important;
  animation: popup-swipe-hint 2.8s ease-in-out infinite;
}

@keyframes popup-swipe-hint {
  0%,
  62%,
  100% {
    transform: translateX(0);
  }
  72% {
    transform: translateX(0.1875rem);
  }
  84% {
    transform: translateX(-0.1875rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .swipe-guide__icon {
    animation: none;
  }

  .swipe-progress__dot {
    transition: none;
  }
}

@media (max-width: 22.5rem) {
  .mobile-swipe-guide {
    align-content: center;
    flex-wrap: wrap;
    gap: 0.375rem 0.75rem;
  }
}

::v-deep .v-carousel__item {
  height: 100% !important;
}

/* --- Image Styling --- */
.image-wrapper {
  position: relative;
  /* Bildformat erzwingen */
  aspect-ratio: 16 / 9;
  width: calc(100% - 32px); /* Padding beachten */

  /* Fallback für alte Browser */
  @supports not (aspect-ratio: 16 / 9) {
    padding-top: 56.25%;
    height: 0;
    .v-image {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
    }
  }
}

.image-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40%;
  background: linear-gradient(to top, rgba(30, 30, 36, 1), transparent);
}

/* --- Text Content --- */
.text-content-wrapper {
  /* Ermöglicht Scrollen innerhalb des Textbereichs */
  overflow-y: auto;

  /* Scrollbar Styling */
  scrollbar-width: thin;
  scrollbar-color: #444 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #444;
    border-radius: 3px;
  }

  ::v-deep a {
    color: #4caf50 !important;
    font-weight: bold;
    text-decoration: none;
    border-bottom: 1px dashed currentColor;
  }
}

/* --- UI Elements --- */
.backdrop-blur {
  background: rgba(0, 0, 0, 0.3) !important;
  backdrop-filter: blur(10px);
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  color: white !important;
  &.left {
    left: 10px;
  }
  &.right {
    right: 10px;
  }
}

/* --- FAB (Minimized State) --- */
.minimized-fab-container {
  position: fixed;
  bottom: 100px;
  right: 20px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.notification-fab {
  border: 2px solid rgba(var(--v-primary-base), 0.5);
}

.fab-label {
  font-size: 0.75rem;
  font-weight: bold;
  color: white;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
}

.pulse-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid var(--v-primary-base);
  animation: pulse 2s infinite;
  opacity: 0;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}

@media (min-width: 768px) {
  .minimized-fab-container {
    bottom: 40px;
    right: 40px;
  }
}

/* --- Premium CTA Button --- */
.cta-premium-btn {
  background: #ffffff !important; /* Strahlendes Weiss für extremen Kontrast im Dark Mode */
  color: #121212 !important; /* Dunkle Schrift */
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.9);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  /* Der Shine-Effekt */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.05) 50%, rgba(255, 255, 255, 0) 100%);
    transform: skewX(-25deg);
    animation: shine-animation 4s infinite;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3) !important;
  }
}

@keyframes shine-animation {
  0% {
    left: -100%;
  }
  20% {
    left: 200%;
  }
  100% {
    left: 200%;
  }
}

.transition-fast-in-fast-out {
  transition: background-color 0.3s ease, color 0.3s ease;
}
</style>
